import {
  ChangeDetectorRef,
  Component,
  computed,
  effect,
  inject,
  input,
  output,
  ViewChild,
  ChangeDetectionStrategy,
} from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { ContractService } from '@app/features/contracts/contract-service';
import { Contract } from '@app/features/contracts/contract.model';
import { SaveButton } from '@app/shared/buttons/save-button/save-button';
import { XIconButton } from '@app/shared/buttons/x-icon-button/x-icon-button';
import { RxjsDirective } from '@app/shared/rxjs.directive';
import { DocumentService } from '../document-service';
import { SocialSecurityDocumentAndNumber } from '../social-security-document-and-number/social-security-document-and-number';

export interface SocialSecurityDocumentAndNumberDialogData {
  contractId: string;
}

@Component({
  selector: 'app-social-security-document-and-number-dialog',
  imports: [
    SocialSecurityDocumentAndNumber,
    MatDialogModule,
    SaveButton,
    MatButtonModule,
    XIconButton,
    MatProgressSpinnerModule,
  ],
  templateUrl: './social-security-document-and-number-dialog.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './social-security-document-and-number-dialog.scss',
})
export class SocialSecurityDocumentAndNumberDialog extends RxjsDirective {
  readonly dialogRef = inject(MatDialogRef<SocialSecurityDocumentAndNumberDialog>, {
    optional: true,
  });

  readonly injectedData = inject<SocialSecurityDocumentAndNumberDialogData | undefined>(
    MAT_DIALOG_DATA,
    { optional: true },
  );
  readonly inputData = input<SocialSecurityDocumentAndNumberDialogData>();
  readonly data = computed<SocialSecurityDocumentAndNumberDialogData>(() => {
    const data = this.injectedData ?? this.inputData();
    if (!data) {
      throw new Error('SocialSecurityDocumentAndNumberDialogData is required');
    }
    return data;
  });

  readonly saved = output<Contract>();
  readonly canceled = output<void>();

  readonly cdr = inject(ChangeDetectorRef);
  readonly contractService = inject(ContractService);
  readonly documentService = inject(DocumentService);
  contract?: Contract;

  @ViewChild(SocialSecurityDocumentAndNumber)
  socialSecurityDocumentAndNumber?: SocialSecurityDocumentAndNumber;

  get isValid() {
    return this.socialSecurityDocumentAndNumber?.form.valid ?? false;
  }

  constructor() {
    super();
    effect((onCleanup) => {
      const contractId = this.data().contractId;
      this.register(
        this.contractService.findOne(contractId).subscribe((contract) => {
          this.contract = contract;
          this.cdr.markForCheck();
        }),
      );
    });
  }

  onOk() {
    if (!this.isValid) {
      return;
    }

    const res$ = this.socialSecurityDocumentAndNumber?.addDocumentToContractAndUpdateSSNumber();
    if (!res$) {
      this.dialogRef?.close();
      this.canceled.emit();
      return;
    }
    this.register(
      res$.subscribe((c: Contract) => {
        this.dialogRef?.close(c);
        this.saved.emit(c);
        this.documentService.closePreview();
      }),
    );
  }

  close() {
    this.dialogRef?.close();
    this.canceled.emit();
  }
}
